import { useEffect, useMemo, useState } from "react";
import { Modal, ModalBody } from "reactstrap";

import {
  getMcapLicensingCohort,
  getMcapLicensingPayments,
  getMcapLicensingActivity,
  getMcapLicensingExport,
} from "api/api.service";
import useLicensingQuery from "./useLicensingQuery";
import {
  ACTIVITY_CATEGORIES,
  ACTIVITY_LABELS,
  RISK,
  billing as billingOf,
  access as accessOf,
  trial as trialOf,
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  relativeDays,
} from "./licensingTheme";

const PAGE_SIZE = 25;

const COHORT_COLUMNS = [
  { key: "name", label: "Teacher" },
  { key: "school", label: "School" },
  { key: "billingState", label: "Plan" },
  { key: "trialDaysLeft", label: "Trial" },
  { key: "renewsOn", label: "Next renewal" },
  { key: "collected", label: "Paid so far", align: "right" },
  { key: "lastPayment", label: "Last payment" },
];

const PAYMENT_COLUMNS = [
  { key: "date", label: "When" },
  { key: null, label: "Invoice" },
  { key: null, label: "Who" },
  { key: "status", label: "Status" },
  { key: "amount", label: "Amount", align: "right" },
];

const ACTIVITY_COLUMNS = [
  { key: null, label: "When" },
  { key: null, label: "What happened" },
  { key: null, label: "Who" },
  { key: null, label: "School" },
];

// Keys in overview.risk, against the risk filter the cohort endpoint takes.
const RISK_CARDS = [
  { key: "grace", countKey: "grace", tone: "risk" },
  { key: "cancelling_30d", countKey: "cancelling30d", tone: "warn" },
  { key: "paid_no_record", countKey: "paidNoRecord", tone: "risk" },
  { key: "renewal_overdue", countKey: "renewalOverdue", tone: "warn" },
  { key: "no_card", countKey: "noCard", tone: "warn" },
];

function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function StateChip({ state }) {
  const meta = billingOf(state);
  return (
    <span className="mcap-lic-state" style={{ "--chip": meta.color }}>
      {meta.label}
    </span>
  );
}

/**
 * The people, payments or events behind whatever was clicked.
 *
 * At-risk opens as cards first, one per kind of risk with its count, so the
 * empty ones are visibly empty instead of being a table to open and find
 * nothing in. Everything else opens straight to its table.
 *
 * Nothing here is cached anywhere: the server sends it no-store, and this
 * component holds it in memory only while the dialog is open.
 */
export default function LicensingDialog({ drill, filters, risk, onClose, onDrill, onBack }) {
  const type = drill?.type;
  const isPayments = type === "payments";
  const isActivity = type === "activity";
  const isRisk = type === "risk";
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(isPayments ? "date" : "name");
  const [order, setOrder] = useState(isPayments ? "desc" : "asc");
  const [category, setCategory] = useState(drill?.filters?.category || "");
  const [searchInput, setSearchInput] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const search = useDebounced(searchInput);

  const drillKey = JSON.stringify(drill?.filters ?? {});
  const filtersKey = JSON.stringify(filters ?? {});

  useEffect(() => {
    setPage(1);
  }, [search, sort, order, category, drillKey]);

  const params = useMemo(() => {
    const base = { ...filters, ...(drill?.filters ?? {}), page: String(page), limit: String(PAGE_SIZE) };
    if (isActivity) {
      const { category: _ignored, startDate, endDate, ...rest } = base;
      return { ...rest, ...(category ? { category } : {}) };
    }
    return { ...base, ...(search ? { search } : {}), sort, order };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, drillKey, search, sort, order, page, category, isActivity]);

  const fetcher = isPayments ? getMcapLicensingPayments : isActivity ? getMcapLicensingActivity : getMcapLicensingCohort;
  const { data, loading, error } = useLicensingQuery(fetcher, type || "none", params, { skip: !drill || isRisk });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const columns = isPayments ? PAYMENT_COLUMNS : isActivity ? ACTIVITY_COLUMNS : COHORT_COLUMNS;
  const noun = isPayments ? "payment" : isActivity ? "event" : "teacher";

  const toggleSort = (key) => {
    if (sort === key) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setSort(key);
      setOrder(["collected", "amount", "date", "renewsOn", "lastPayment"].includes(key) ? "desc" : "asc");
    }
  };

  const download = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await getMcapLicensingExport({ ...params, dataset: isPayments ? "payments" : "cohort", page: "1", limit: "10000" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `mcap-licensing-${isPayments ? "payments" : "teachers"}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err);
    } finally {
      setExporting(false);
    }
  };

  if (!drill) return null;

  return (
    <Modal isOpen toggle={onClose} size="xl" scrollable centered className="mcap-lic-modal" aria-labelledby="mcap-lic-dialog-title">
      <ModalBody>
        <header className="mcap-lic-dialog-head">
          <div>
            {onBack && (
              <button type="button" className="mcap-lic-back" onClick={onBack}>
                ← Back
              </button>
            )}
            <h2 id="mcap-lic-dialog-title" className="mcap-lic-dialog-title">
              {isRisk ? "Needs attention" : drill.title || "Details"}
            </h2>
            <p className="mcap-lic-dialog-sub">
              {isRisk
                ? "Click a card to see who. A teacher can be in more than one."
                : loading
                  ? "Loading…"
                  : `${formatNumber(total)} ${noun}${total === 1 ? "" : "s"}`}
            </p>
          </div>
          <div className="mcap-lic-dialog-tools">
            {!isRisk && !isActivity && (
              <>
                <input
                  type="search"
                  className="mcap-lic-input"
                  placeholder={isPayments ? "Invoice, name, email or school" : "Name or email"}
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  aria-label="Search these rows"
                />
                <button type="button" className="mcap-lic-btn" onClick={download} disabled={exporting || !total}>
                  {exporting ? "Preparing…" : "Download CSV"}
                </button>
              </>
            )}
            <button type="button" className="mcap-lic-btn is-ghost" onClick={onClose}>
              Close
            </button>
          </div>
        </header>

        {exportError && <p className="mcap-lic-error">The CSV could not be downloaded.</p>}

        {isRisk && (
          <div className="mcap-lic-risk-grid">
            {RISK_CARDS.map((card) => {
              const count = risk?.[card.countKey] ?? 0;
              const meta = RISK[card.key];
              return (
                <button
                  key={card.key}
                  type="button"
                  className={`mcap-lic-risk-card ${count ? `is-${card.tone}` : "is-clear"}`}
                  disabled={!count}
                  onClick={() => onDrill({ type: "cohort", title: meta.label, filters: { risk: card.key } }, { fromRisk: true })}
                >
                  <span className="mcap-lic-risk-count">{formatNumber(count)}</span>
                  <span className="mcap-lic-risk-label">{meta.label}</span>
                  <span className="mcap-lic-risk-detail">{count ? meta.detail : "Nobody right now."}</span>
                  {count > 0 && <span className="mcap-lic-risk-open">See who</span>}
                </button>
              );
            })}
          </div>
        )}

        {isActivity && (
          <div className="mcap-lic-chips" role="group" aria-label="Kind of event">
            {[["", "All"], ...Object.entries(ACTIVITY_CATEGORIES).map(([k, v]) => [k, v.label])].map(([key, label]) => (
              <button key={key || "all"} type="button" className={category === key ? "is-active" : ""} onClick={() => setCategory(key)}>
                {label}
              </button>
            ))}
          </div>
        )}

        {!isRisk && error && <p className="mcap-lic-error">These rows could not be loaded. The filters are unchanged — try again.</p>}

        {!isRisk && !error && (
          <div className="mcap-lic-table-wrap">
            <table className="mcap-lic-table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.label}
                      scope="col"
                      className={column.align === "right" ? "is-right" : ""}
                      aria-sort={column.key && sort === column.key ? (order === "asc" ? "ascending" : "descending") : undefined}
                    >
                      {column.key ? (
                        <button type="button" onClick={() => toggleSort(column.key)}>
                          {column.label}
                          {sort === column.key && <span aria-hidden="true">{order === "asc" ? " ↑" : " ↓"}</span>}
                        </button>
                      ) : (
                        column.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading &&
                  Array.from({ length: 6 }, (_, i) => (
                    <tr key={`s${i}`} className="is-skeleton">
                      <td colSpan={columns.length}>
                        <span />
                      </td>
                    </tr>
                  ))}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="mcap-lic-table-loading">
                      Nothing matches this.
                    </td>
                  </tr>
                )}
                {!loading &&
                  rows.map((row) => {
                    if (isActivity) {
                      const categoryMeta = ACTIVITY_CATEGORIES[row.category];
                      return (
                        <tr key={row.id}>
                          <td>
                            {formatDateTime(row.at)}
                            <span className="mcap-lic-who-sub">{relativeDays(row.at)}</span>
                          </td>
                          <td>
                            <span className="mcap-lic-state" style={{ "--chip": categoryMeta?.color || "#8a9992" }}>
                              {categoryMeta?.label || row.category}
                            </span>
                            <span className="mcap-lic-who-sub">
                              {row.action === "LICENSE_AUTO_RENEW_CHANGED" && row.autoRenew !== null
                                ? `turned auto-renew ${row.autoRenew ? "back on" : "off"}`
                                : ACTIVITY_LABELS[row.action] || row.action}
                              {row.repeats > 1 ? ` · ${row.repeats} attempts` : ""}
                              {row.reason ? ` · ${row.reason}` : ""}
                            </span>
                          </td>
                          <td>
                            <span className="mcap-lic-who">{row.userName || (row.licenseId ? "School licence" : "—")}</span>
                            <span className="mcap-lic-who-sub">{row.userEmail || ""}</span>
                          </td>
                          <td>{row.schoolName || "—"}</td>
                        </tr>
                      );
                    }
                    if (isPayments) {
                      return (
                        <tr key={row.id}>
                          <td>{formatDateTime(row.paidAt || row.createdAt)}</td>
                          <td>{row.invoiceNumber || "—"}</td>
                          <td>
                            <span className="mcap-lic-who">{row.userName || row.schoolName || "—"}</span>
                            <span className="mcap-lic-who-sub">{row.userEmail || (row.licenseId ? "School licence" : "")}</span>
                          </td>
                          <td>
                            <span className={`mcap-lic-pay-status is-${row.kind}`}>{row.kind}</span>
                            {row.failureReason && <span className="mcap-lic-who-sub">{row.failureReason}</span>}
                          </td>
                          <td className="is-right">{formatMoney(row.totalCents, row.currency)}</td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={row.userId}>
                        <td>
                          <span className="mcap-lic-who">{row.name}</span>
                          <span className="mcap-lic-who-sub">{row.email}</span>
                        </td>
                        <td>{row.schoolName || "—"}</td>
                        <td>
                          <StateChip state={row.billingState} />
                          <span className="mcap-lic-who-sub">{accessOf(row.accessSource).label}</span>
                        </td>
                        <td>
                          {row.trialDaysLeft !== null && row.trialDaysLeft !== undefined && row.trialDaysLeft > 0
                            ? `${row.trialDaysLeft} day${row.trialDaysLeft === 1 ? "" : "s"} left`
                            : trialOf(row.trialState).label}
                        </td>
                        <td>
                          {row.renewsOn ? (
                            <>
                              {formatDate(row.renewsOn, { withYear: true })}
                              <span className="mcap-lic-who-sub">
                                {relativeDays(row.renewsOn)}
                                {row.autoRenew === false ? " · won't renew" : row.autoRenew ? " · renews" : ""}
                              </span>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="is-right">{formatMoney(row.collectedCents, row.currency || "USD")}</td>
                        <td>
                          {row.lastPaymentStatus ? (
                            <>
                              {row.lastPaymentStatus === "SUCCEEDED" ? "Paid" : "Failed"}
                              <span className="mcap-lic-who-sub">{formatDate(row.lastPaymentAt)}</span>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}

        {!isRisk && pages > 1 && (
          <nav className="mcap-lic-pager" aria-label="Pages">
            <button type="button" className="mcap-lic-btn is-ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
              Previous
            </button>
            <span>
              Page {page} of {pages}
            </span>
            <button type="button" className="mcap-lic-btn is-ghost" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}>
              Next
            </button>
          </nav>
        )}
      </ModalBody>
    </Modal>
  );
}
